import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
  Toolbar,
} from "@mui/x-data-grid-premium";
import { useFetch } from "../../../../services/hooks/useFetch";
import AddIcon from "@mui/icons-material/Add";
import { apiPrefix } from "../../../../utils/main";
import FlowsheetGroupDetails from "./FlowsheetGroupDetails";
import { useSearchParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";

export default function FlowsheetGroupsPage() {
  function newFlowsheetGroup() {
    searchParams.set("mode", "create");
    setFlowsheetGroup({});
    window.history.pushState({}, "", "?" + searchParams.toString());
  }

  function CustomToolBar() {
    return (
      <Toolbar>
        <Button
          endIcon={<AddIcon />}
          size="medium"
          onClick={() => newFlowsheetGroup()}
        >
          NEW GROUP
        </Button>
      </Toolbar>
    );
  }

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("");
  const [shouldFetchFlowsheets, setShouldFetchFlowsheets] = useState(true);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [flowsheetGroup, setFlowsheetGroup] = useState<
    FlowsheetGroup | Partial<FlowsheetGroup> | undefined
  >(undefined);

  const {
    data: flowsheetGroupsList,
    fetch: fetchFlowsheetGroups,
    isLoading: flowsheetGroupsLoading,
  } = useFetch<PagedResponse<FlowsheetGroup>>();

  const flowsheetGroupTableFields: GridColDef<FlowsheetGroup>[] = [
    {
      field: "name",
      valueGetter: (_value, row) => {
        return row.name;
      },
      headerName: "Group Name",
      width: 400,
    },

    {
      field: "inUse",
      renderCell: (params) =>
        params.row.inUse ? <CheckIcon fontSize={"small"} /> : "",
      align: "center",
      headerAlign: "center",
      display: "flex",
      headerName: "In use",
      width: 75,
    },
    {
      field: "sourceSchemaIdentifier",
      valueGetter: (_value, row) => {
        return row.sourceSchemaIdentifier;
      },
      headerName: "Schema",
      width: 250,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      flex: 1,
      align: "center",
      getActions: ({ row }: GridRowParams<FlowsheetGroup>) => {
        return [
          <GridActionsCellItem
            key={row.id}
            icon={<ChevronRightIcon fontSize="small" />}
            label="View Flowsheet Details"
            className="textPrimary"
            onClick={() => {
              searchParams.set("name", row.name);
              searchParams.set("mode", "modify");
              setSearchParams(searchParams);
              setFlowsheetGroup(row);
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (shouldFetchFlowsheets && !flowsheetGroupsLoading) {
      const params = new URLSearchParams();
      params.set("page", paginationModel.page.toString());
      params.set("limit", paginationModel.pageSize.toString());
      if (sortModel.length > 0) {
        params.set(
          "sort",
          sortModel
            .map((s) => `${s.sort === "desc" ? "-" : "+"}${s.field}`)
            .join(","),
        );
      }
      if (filter !== "") {
        params.set("filter", filter);
      }
      void fetchFlowsheetGroups(`${apiPrefix}/flowsheets?${params.toString()}`);
      setShouldFetchFlowsheets(false);
    }
  }, [
    shouldFetchFlowsheets,
    fetchFlowsheetGroups,
    flowsheetGroupsLoading,
    paginationModel,
    sortModel,
    filter,
  ]);

  return (
    <Grid
      container
      direction="column"
      className="company-settings-item"
      sx={{ flex: 1 }}
    >
      {flowsheetGroup &&
      (searchParams.get("mode") === "create" ||
        searchParams.get("mode") === "modify") ? (
        <FlowsheetGroupDetails flowsheetGroup={flowsheetGroup} />
      ) : (
        <div
          style={{
            height: "100%",
            minHeight: "400px",
            maxWidth: "calc(90vw - 15px)",
          }}
        >
          <Box className={"flowsheet-groups"}>
            <Grid>
              <Typography variant="h4" textAlign="left">
                Flowsheet Groups
              </Typography>
            </Grid>
            <br />
            <Grid>
              <Typography variant="body2">Manage Flowsheet groups</Typography>
            </Grid>
            <br />

            <DataGridPremium
              disableColumnFilter
              disableColumnResize
              disableAggregation
              disableRowGrouping
              disablePivoting
              disableColumnPinning
              pageSizeOptions={[5, 10, 20]}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={(model: GridPaginationModel) => {
                setShouldFetchFlowsheets(true);
                setPaginationModel(model);
              }}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={(model: GridSortModel) => {
                setShouldFetchFlowsheets(true);
                setSortModel(model);
              }}
              filterMode="server"
              onFilterModelChange={(filterModel) => {
                setShouldFetchFlowsheets(true);
                setFilter(filterModel.quickFilterValues?.join(" ") || "");
              }}
              rowCount={flowsheetGroupsList?.rowCount || 0}
              loading={flowsheetGroupsLoading}
              showToolbar={true}
              slots={{
                toolbar: CustomToolBar,
              }}
              slotProps={{
                loadingOverlay: {
                  variant: "linear-progress",
                },
              }}
              columnHeaderHeight={60}
              rowHeight={60}
              getRowId={(row) => row.id}
              density="compact"
              columns={flowsheetGroupTableFields}
              rows={flowsheetGroupsList?.results || []}
              rowSelection={false}
            />
          </Box>
        </div>
      )}
    </Grid>
  );
}
